'use client';

import LanguageContext from '@/context/language';
import { userService } from '@/services';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { getAppConfigByCountry, setUserInfo } from './utils/set-user-info';
import { useAppStore } from '../store/app-store';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAsyncEffect } from 'ahooks';
import { useMessage } from '@/shared/components';
import MessageContext from '@/context/message.context';
import Track from '@/shared/tracking/track';

const Providers = ({ lang, children }) => {
  const { MessageComponent, showMessage } = useMessage();
  const { setInitialed } = useAppStore();
  const [language, setLanguage] = useState(lang);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      })
  );
  const { setShipBillInfo, setIsAnonymous, setConfigByCountry } = useAppStore();

  useAsyncEffect(async () => {
    await setAppUserInfo();
    await getConfigByCountry();
    setInitialed(true);
  }, []);

  const setAppUserInfo = async () => {
    const res = await userService.getUserInfo();
    if (res.code === '200') {
      setUserInfo(res.data);
      const isAnonymous = res.data?.userDetails?.userType === 'ANONYMOUS';
      if (!isAnonymous) {
        setIsAnonymous(false);
        await getShipBillInfo();
      }
    }
  };

  const getShipBillInfo = async () => {
    const res = await userService.getShipBillInfo();
    if (res.code === '200') {
      const shipToList = res.data.shipToList ?? [];
      const billTo: any = res.data.billTo ?? {};
      const userInvoiceEmails: any = res.data.userInvoiceEmails ?? {};
      setShipBillInfo(billTo, shipToList, userInvoiceEmails);
    }
  };

  const getConfigByCountry = async () => {
    const res = await userService.getConfigByCountry();
    const config = getAppConfigByCountry(res?.data || []);
    setConfigByCountry(config);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Track lang={lang}>
        {MessageComponent}
        <ReactQueryDevtools initialIsOpen={false} />
        <LanguageContext.Provider value={{ lang: language, setLanguage }}>
          <MessageContext.Provider value={{ showMessage }}>{children}</MessageContext.Provider>
        </LanguageContext.Provider>
      </Track>
    </QueryClientProvider>
  );
};

export default Providers;
